import { FC, FormEvent, useContext, useEffect, useState } from "react";
import { Modal } from "reactstrap";
import { loginConCorreo } from "./loginConCorreo";
import HostApi from "../../../../data/settings/HostApi";
import UserContext from "../../../../domain/providers/user/UserContext";
import { trackEvent } from "../../../../lib/analytics";
import Button from "../Button";
import { GoogleAuthButton } from "../googleAuthButton";
import "./AuthGateModalStyles.scss";

type Vista = "inicio" | "login" | "registro" | "olvide" | "codigo" | "nueva_clave";

const TITULOS: Record<Vista, string> = {
    inicio: "",
    login: "Inicia sesión",
    registro: "Crea tu cuenta",
    olvide: "Recupera tu contraseña",
    codigo: "Ingresa el código",
    nueva_clave: "Nueva contraseña",
};

const mensajeDe = (e: any): string => e?.error ?? e?.message ?? e?.detail ?? "Ha ocurrido un error";

export type OrigenAcceso = "tarjeta_vehiculo" | "detalle_url" | "favorito";

interface Props {
    /** Sin `onClose` el modal es obligatorio: no se puede descartar (ni Esc ni clic en el fondo). */
    onClose?: () => void;
    /** Si viene, se muestra "Volver al catálogo" (útil cuando el modal no se puede descartar). */
    onVolver?: () => void;
    /** Se llama justo después de iniciar sesión o registrarse. */
    onSuccess?: () => void;
    vehiculoId?: string;
    /** Desde dónde se mostró; sin él no se emiten los eventos de "muro" (p. ej. la página de login). */
    origen?: OrigenAcceso;
    /** Renderiza el contenido como tarjeta dentro de la página, sin modal. */
    enPagina?: boolean;
    /** Vista con la que abre; por defecto la pantalla de bienvenida. */
    vistaInicial?: "inicio" | "login" | "registro" | "olvide";
    mensaje?: string;
}

// Modal reutilizable de login / registro / recuperación de contraseña. Quien lo use decide
// cuándo mostrarlo (ver `useRequireAuth`) y qué hacer al terminar (`onSuccess`).
const AuthGateModal: FC<Props> = ({
    onClose,
    onVolver,
    onSuccess,
    vehiculoId,
    origen,
    enPagina = false,
    vistaInicial = "inicio",
    mensaje = "Inicia sesión para ver el detalle de este vehículo y su peritaje",
}) => {
    const { setUser } = useContext(UserContext);
    const [vista, setVista] = useState<Vista>(vistaInicial);
    const [campos, setCampos] = useState({ name: "", email: "", phone: "", password: "", code: "" });
    const [error, setError] = useState("");
    const [info, setInfo] = useState("");
    const [loading, setLoading] = useState(false);
    const [verClave, setVerClave] = useState(false);

    useEffect(() => {
        if (origen) trackEvent("acceso_requerido_mostrado", { vehiculo_id: vehiculoId, origen });
    }, [vehiculoId, origen]);

    const set = (k: keyof typeof campos) => (e: React.ChangeEvent<HTMLInputElement>) =>
        setCampos((c) => ({ ...c, [k]: e.target.value }));
    const ir = (v: Vista, mensaje = "") => {
        setError("");
        setInfo(mensaje);
        setVista(v);
    };

    const login = async () => setUser(await loginConCorreo(campos.email, campos.password));

    const enviar = async (e: FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        try {
            if (vista === "login") {
                await login();
                trackEvent("sesion_iniciada", { metodo: "email" });
                onSuccess?.();
            } else if (vista === "registro") {
                await HostApi.post("/register-customer/", {
                    name: campos.name,
                    email: campos.email,
                    phone: campos.phone,
                    password: campos.password,
                });
                await login();
                trackEvent("registro_completado", { metodo: "email" });
                onSuccess?.();
            } else if (vista === "olvide") {
                await HostApi.post("/send-otp-password-reset/", { email: campos.email });
                trackEvent("recuperacion_clave_solicitada", {});
                ir("codigo", "Te enviamos un código a tu correo.");
            } else if (vista === "codigo") {
                await HostApi.post("/verify-otp-password-reset/", { email: campos.email, code: campos.code });
                ir("nueva_clave");
            } else {
                await HostApi.post("/reset-password/", {
                    email: campos.email,
                    code: campos.code,
                    new_password: campos.password,
                });
                trackEvent("recuperacion_clave_completada", {});
                setCampos((c) => ({ ...c, password: "", code: "" }));
                ir("login", "Contraseña actualizada. Inicia sesión.");
            }
        } catch (err) {
            const motivo = mensajeDe(err);
            setError(motivo);
            if (vista === "login") trackEvent("sesion_inicio_fallido", { metodo: "email", motivo_error: motivo });
            else if (vista === "registro") trackEvent("registro_fallido", { motivo_error: motivo });
            else if (vista !== "inicio")
                trackEvent("recuperacion_clave_fallida", { paso: vista, motivo_error: motivo });
        } finally {
            setLoading(false);
        }
    };

    // Sin botón "Cerrar": con `onClose` solo se descarta con Esc o clic en el fondo.
    const cerrar = () => {
        if (origen) trackEvent("acceso_requerido_abandonado", { vehiculo_id: vehiculoId, origen });
        onClose?.();
    };

    const input = (k: keyof typeof campos, label: string, props: React.InputHTMLAttributes<HTMLInputElement> = {}) => {
        const esClave = props.type === "password";
        return (
            <div className="form-group my-2">
                <label className="mandatory">{label}</label>
                <div className="auth_input_wrap">
                    <input
                        className="form-control"
                        required
                        value={campos[k]}
                        onChange={set(k)}
                        {...props}
                        type={esClave && verClave ? "text" : props.type}
                    />
                    {esClave && (
                        <button
                            type="button"
                            className="auth_toggle_clave"
                            aria-label={verClave ? "Ocultar contraseña" : "Mostrar contraseña"}
                            onClick={() => setVerClave((v) => !v)}
                        >
                            {verClave ? "Ocultar" : "Mostrar"}
                        </button>
                    )}
                </div>
            </div>
        );
    };

    const cuerpo = (
        <div className="auth_modal_body">
            {onClose && (
                <button type="button" className="auth_close" aria-label="Cerrar" onClick={cerrar}>
                    ×
                </button>
            )}
            <img className="auth_logo" src="/assets/logos/horizontal.svg" alt="wcar" />
            {vista === "inicio" ? (
                <>
                    <h4>
                        ¡Ingresa a nuestra
                        <br />
                        <span className="auth_accent">web ahora!</span>
                    </h4>
                    <p className="auth_subtitle">{mensaje}</p>
                    <div className="auth_stack">
                        <Button
                            variant="secondary"
                            label="Continuar con correo electrónico"
                            width="100%"
                            onClick={() => ir("login")}
                        />
                        <GoogleAuthButton largo />
                    </div>
                    <button type="button" className="auth_link auth_forgot" onClick={() => ir("olvide")}>
                        ¿Necesitas ayuda?
                    </button>
                    <p className="auth_terms">
                        Al registrarte, aceptas nuestros{" "}
                        <a href="/terminos-y-condiciones-usuarios" target="_blank" rel="noreferrer">
                            Términos y condiciones
                        </a>{" "}
                        y la{" "}
                        <a href="/politica-de-privacidad" target="_blank" rel="noreferrer">
                            política de privacidad
                        </a>
                    </p>
                </>
            ) : (
                <>
                    <h4>{TITULOS[vista]}</h4>
                    {(vista === "login" || vista === "registro") && <p className="auth_subtitle">{mensaje}</p>}
                    {info && <p className="auth_info">{info}</p>}
                    <form onSubmit={enviar}>
                        {vista === "registro" && input("name", "Nombre completo")}
                        {vista !== "codigo" && vista !== "nueva_clave" && input("email", "Email", { type: "email" })}
                        {vista === "registro" && input("phone", "Teléfono", { type: "tel" })}
                        {(vista === "login" || vista === "registro") &&
                            input("password", "Contraseña", {
                                type: "password",
                                minLength: vista === "registro" ? 8 : 1,
                            })}
                        {vista === "codigo" && input("code", "Código", { inputMode: "numeric", maxLength: 6 })}
                        {vista === "nueva_clave" &&
                            input("password", "Nueva contraseña", { type: "password", minLength: 8 })}
                        {error && <p className="auth_error">{error}</p>}
                        <div className="d-flex justify-content-center mt-3">
                            <Button
                                type="submit"
                                disabled={loading}
                                label={
                                    loading
                                        ? "Enviando..."
                                        : vista === "registro"
                                          ? "Crear cuenta"
                                          : vista === "login"
                                            ? "Iniciar sesión"
                                            : "Continuar"
                                }
                            />
                        </div>
                    </form>
                    {vista === "login" && (
                        <button type="button" className="auth_link auth_forgot" onClick={() => ir("olvide")}>
                            ¿Olvidaste tu contraseña?
                        </button>
                    )}
                    <div className="auth_footer">
                        {vista === "login" ? (
                            <button type="button" className="auth_link" onClick={() => ir("registro")}>
                                ¿No tienes cuenta? Regístrate
                            </button>
                        ) : (
                            <button type="button" className="auth_link" onClick={() => ir("login")}>
                                Volver a iniciar sesión
                            </button>
                        )}
                        <button type="button" className="auth_link" onClick={() => ir("inicio")}>
                            Otras opciones
                        </button>
                    </div>
                </>
            )}
            {onVolver && (
                <button
                    type="button"
                    className="auth_link auth_forgot"
                    onClick={() => {
                        if (origen) trackEvent("acceso_requerido_abandonado", { vehiculo_id: vehiculoId, origen });
                        onVolver();
                    }}
                >
                    ← Volver al catálogo
                </button>
            )}
        </div>
    );

    if (enPagina) return <div className="auth_modal auth_page_card">{cuerpo}</div>;

    return (
        <Modal
            isOpen
            centered
            toggle={onClose && cerrar}
            backdrop={onClose ? true : "static"}
            keyboard={Boolean(onClose)}
            contentClassName="auth_modal"
        >
            {cuerpo}
        </Modal>
    );
};

export default AuthGateModal;
